import * as React from 'react'

import { format as fnsFormat } from 'date-fns'
import { MobXProviderContext, observer } from 'mobx-react'

import type { IStruxTagUtilizationQueryVariables } from '~/client/graph/operations/generated/StruxTagUtilization.generated'
import StruxTagHistoryStore from '~/client/src/shared/stores/domain/StruxTagHistory.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'

import Localization from '../../localization/LocalizationManager'
import { Loader } from '../Loader'
import UtilizationChart, { IUtilizationChartDay } from './UtilizationChart'

const UTILIZATION_WINDOW_DAYS = 7

interface IProps {
  projectId?: string
  infuseId?: string | null
}

interface IMobXContext {
  projectDateStore?: ProjectDateStore
  struxTagHistoryStore: StruxTagHistoryStore
}

const StruxTagUtilizationView: React.FC<IProps> = ({ projectId, infuseId }) => {
  const { projectDateStore, struxTagHistoryStore } = React.useContext(
    MobXProviderContext,
  ) as IMobXContext

  const variables: IStruxTagUtilizationQueryVariables = {
    projectId: projectId || '',
    infuseId: infuseId || '',
    windowDays: UTILIZATION_WINDOW_DAYS,
  }
  const historyState = struxTagHistoryStore.utilizationFor(variables)

  React.useEffect(() => {
    if (!projectId || !infuseId) return
    void struxTagHistoryStore.ensureUtilization({
      projectId,
      infuseId,
      windowDays: UTILIZATION_WINDOW_DAYS,
    })
  }, [infuseId, projectId, struxTagHistoryStore])

  const data = historyState._tag === 'Ready' ? historyState.data : null
  const loading =
    !!projectId &&
    !!infuseId &&
    (historyState._tag === 'NotAsked' || historyState._tag === 'Loading')
  const error = historyState._tag === 'Failed' ? historyState.error : null

  const utilization = React.useMemo(() => {
    const result = data?.struxTagUtilization
    if (!result || !projectDateStore) return null
    const days: IUtilizationChartDay[] = result.days.map(day => {
      const [y, m, d] = String(day.date).split('-').map(Number)
      const dateObj = new Date(y, m - 1, d)
      return {
        key: dateObj.getTime(),
        shortLabel: fnsFormat(dateObj, projectDateStore.currentFormat.weekday, {
          locale: Localization.dateLocale,
        }),
        fullLabel: fnsFormat(
          dateObj,
          projectDateStore.currentFormat.monthDayAndYear,
          { locale: Localization.dateLocale },
        ),
        hours: day.hours,
        overtimeHours: day.overtimeHours,
        isWorkingDay: day.isWorkingDay,
        isToday: day.isToday,
        hasOvertime: day.overtimeHours > 0,
      }
    })
    return {
      days,
      shiftHours: result.shiftHours,
      totalHours: result.totalHours,
      percentDaysUsed: result.percentDaysUsed ?? null,
      utilizationRate: result.utilizationRate ?? null,
    }
  }, [data, projectDateStore])

  if (!infuseId) {
    return (
      <div className="tw:text-body-md tw:text-muted-foreground">
        {Localization.translator.noStruxTagLinked}
      </div>
    )
  }

  if (loading) return <Loader />

  if (error) {
    return (
      <div className="tw:text-body-md tw:text-destructive">
        {Localization.translator.somethingWentWrongTryAgainLater}
      </div>
    )
  }

  if (!utilization) return null

  return (
    <UtilizationChart
      days={utilization.days}
      shiftHours={utilization.shiftHours}
      totalHours={utilization.totalHours}
      percentDaysUsed={utilization.percentDaysUsed}
      utilizationRate={utilization.utilizationRate}
    />
  )
}

export default observer(StruxTagUtilizationView)
